import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

const MODELS = {
  anthropic: ["claude-opus-5-5", "claude-sonnet-5-5", "claude-opus-5", "claude-sonnet-4-6", "claude-haiku-4-5-20251001"],
  openai: ["gpt-6-luna", "gpt-6-sol", "gpt-5.6-terra", "gpt-5.5", "gpt-5.4"],
  gemini: ["gemini-3.1-pro-preview", "gemini-3.8-flash", "gemini-3-flash-preview", "gemini-2.5-pro"],
};
const NUMS = [
  ["must_have_pct", "Tier 1 threshold (% of JDs; top 12 keywords always count)"], ["match_target_pct", "Weighted match target (%)"],
  ["max_panel_rounds", "Max recruiter panel rounds (Phase 2)"], ["min_usable_jds", "Minimum usable JDs to continue (target stays 50)"],
  ["jd_target", "JDs per set"], ["jd_days", "Default JD posted window (days)"], ["min_numeric_facts", "Verified numeric facts per role (no longer required at Intake)"],
];
const inp = "w-full bg-slate-900 border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-blue-500";

export default function Settings() {
  const [s, setS] = useState(null);
  const [word, setWord] = useState("");
  useEffect(() => { api.get("/settings").then(({ data }) => setS(data)); }, []);
  if (!s) return null;
  const set = (k, v) => setS({ ...s, [k]: v });
  const save = async () => {
    try { const { id, ...body } = s; const { data } = await api.put("/settings", body); setS(data); toast.success("Settings saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const addWord = () => { const w = word.trim().toLowerCase(); if (w && !s.banned_words.includes(w)) set("banned_words", [...s.banned_words, w]); setWord(""); };
  return (
    <div className="px-10 py-8 max-w-[1100px] space-y-6">
      <div className="flex items-end"><div><div className="label-xs mb-1">Configuration</div><h1 className="text-3xl font-extrabold tracking-tight">Settings</h1></div>
        <Button data-testid="settings-save-button" onClick={save} className="ml-auto bg-blue-600 hover:bg-blue-500 text-white">Save settings</Button></div>
      <div className="card-surface grid grid-cols-3 gap-4">
        <div className="col-span-3 font-semibold">AI model</div>
        <label><span className="label-xs block mb-1">Provider</span>
          <select data-testid="settings-provider-select" className={inp} value={s.provider} onChange={(e) => setS({ ...s, provider: e.target.value, model: MODELS[e.target.value][0] })}>
            {Object.keys(MODELS).map((p) => <option key={p}>{p}</option>)}</select></label>
        <label><span className="label-xs block mb-1">Model name</span>
          <input data-testid="settings-ai-model-select" list="models" className={inp} value={s.model} onChange={(e) => set("model", e.target.value)} />
          <datalist id="models">{(MODELS[s.provider] || []).map((m) => <option key={m} value={m} />)}</datalist></label>
        <label><span className="label-xs block mb-1">Temperature: {s.temperature}</span>
          <input data-testid="settings-temperature-input" type="range" min="0" max="1" step="0.1" value={s.temperature} onChange={(e) => set("temperature", Number(e.target.value))} className="w-full accent-blue-500 mt-2" /></label>
        <div className="col-span-3 text-xs text-muted-foreground">Some models (e.g. Claude Opus 5.5) only accept their default temperature. In that case the app uses the default automatically. Fast yes/no decisions (JD level, keyword category, gap check) use Jev.</div>
      </div>
      <div className="card-surface grid grid-cols-2 gap-4">
        <div className="col-span-2 font-semibold">Thresholds</div>
        {NUMS.map(([k, l]) => (
          <label key={k}><span className="label-xs block mb-1">{l}</span><input data-testid={`settings-${k}`} type="number" className={inp} value={s[k]} onChange={(e) => set(k, Number(e.target.value))} /></label>
        ))}
      </div>
      <div className="card-surface">
        <div className="font-semibold mb-3">Banned / AI-tell words</div>
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid="banned-word-list">
          {s.banned_words.map((w) => (
            <span key={w} className="text-xs px-2 py-1 rounded-md bg-slate-800 inline-flex items-center gap-1">{w}
              <button onClick={() => set("banned_words", s.banned_words.filter((x) => x !== w))} className="text-slate-500 hover:text-rose-400"><X size={11} /></button></span>
          ))}
        </div>
        <div className="flex gap-2"><input data-testid="banned-word-input" className={inp} value={word} onChange={(e) => setWord(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addWord()} placeholder="Add a word or phrase" />
          <Button variant="secondary" onClick={addWord} data-testid="banned-word-add">Add</Button></div>
      </div>
    </div>
  );
}
